import React from 'react';
import {
  TaskOverlay, FormField, JalaliDatePicker, Alert, Button, Avatar, Spinner,
} from '@parsina/framework';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { CT } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useReceptionStrings } from '../lib/receptionStrings';
import {
  rescheduleAppointment, iranLocalToEpoch, epochToIranLocal, errorCode,
} from '../hooks/useDoctorBooking';
import { useDirtyClose } from './common/DirtyCloseGuard';
import { userErrorMessage } from '../lib/userErrorMessage';

// ─────────────────────────────────────────────────────────────────────────────
// «تغییر زمان نوبت» (PM-0102) — the doctor moves one of their own booked نوبت‌ها
// to another day or hour.
//
// ONE question in TaskOverlay's two shells (DESIGN_CONCEPT §5.2): when. The
// patient and the clinic are facts of the row and are shown, not asked. The
// same date-and-time picker «ثبت نوبت» uses for «انتخاب دستی», opened on the
// time the نوبت has now.
//
// The row is MOVED — never cancelled and booked again — so its invoice and any
// prepayment stay where they are. The server re-runs the shift and the overlap
// rules at the new instant and re-reads the clinic's booking grant; a refusal
// about the TIME lands on the field (§6.3), anything else in the form's own
// Alert (§2.4: a form open in front of the user reports inline, never a toast).
// Nothing is optimistic: the overlay closes on the server's answer.
// ─────────────────────────────────────────────────────────────────────────────

// Refusals that are about the instant the doctor picked.
const TIME_CODES = new Set(['SLOT_TAKEN', 'DOCTOR_NOT_PRESENT', 'BAD_USER_INPUT']);

export default function RescheduleAppointmentDialog({ open, onClose, apt, onRescheduled, isMobile }) {
  const i18n = useTranslation();
  const { formatDate, formatTime } = i18n;
  const s = useReceptionStrings();

  const current = apt?.dateTime ? epochToIranLocal(apt.dateTime) : '';
  const [when, setWhen] = React.useState(current);
  const [busy, setBusy] = React.useState(false);
  const [whenError, setWhenError] = React.useState('');
  const [formError, setFormError] = React.useState('');

  // Every open starts from the row as it is now — never from the last attempt.
  React.useEffect(() => {
    if (!open) return;
    setWhen(current);
    setBusy(false);
    setWhenError('');
    setFormError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apt?.id]);

  const changed = Boolean(when) && when !== current;
  const { requestClose, guard } = useDirtyClose(changed && !busy, onClose);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy || !apt?.id || !changed) return;
    setFormError('');
    const at = iranLocalToEpoch(when);
    if (at == null) { setWhenError(s('dashboard.book.errors.when')); return; }
    if (at < Date.now() - 5 * 60 * 1000) { setWhenError(s('dashboard.book.errors.past')); return; }
    setBusy(true);
    try {
      const moved = await rescheduleAppointment(apt.id, at);
      const landed = Number(moved?.dateTime) || at;
      onRescheduled?.(moved, {
        dayISO: when.slice(0, 10),
        when: s('dashboard.book.slotAt', { date: formatDate(landed, 'full'), time: formatTime(landed) }),
      });
      onClose?.();
    } catch (err) {
      const message = userErrorMessage(err, i18n, 'errors.save.generic');
      if (TIME_CODES.has(errorCode(err))) setWhenError(message);
      else setFormError(message);
    } finally {
      setBusy(false);
    }
  };

  const name = apt?.name || apt?.patient?.name || i18n.t('common.labels.patient');
  const at = Number(apt?.dateTime);

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <Button variant="outline" type="button" onClick={requestClose} disabled={busy}>
        {s('dashboard.withdraw.keep')}
      </Button>
      <Button
        type="submit"
        form="reschedule-appointment-form"
        disabled={busy || !changed}
        aria-busy={busy || undefined}
        data-testid="reschedule-submit"
      >
        {busy && <Spinner size={16} color="currentColor" />}
        {s('dashboard.reschedule.submit')}
      </Button>
    </div>
  );

  return (
    <>
      <TaskOverlay
        open={open}
        onClose={requestClose}
        title={s('dashboard.reschedule.title')}
        footer={footer}
        busy={busy}
        size="sm"
        isMobile={isMobile}
        data-testid="reschedule-appointment"
      >
        <form
          id="reschedule-appointment-form"
          onSubmit={submit}
          noValidate
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {/* Who and when-now: facts of the row, shown so the doctor is sure which
              نوبت they are moving before they move it. */}
          <div
            data-testid="reschedule-current"
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              border: `1px solid ${C.border}`, borderRadius: 10, background: CT.surface2,
            }}
          >
            <Avatar name={name} size={36} bg={C.navy} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <bdi>{name}</bdi>
              </div>
              {Number.isFinite(at) && at > 0 && (
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                  {s('dashboard.reschedule.current', {
                    when: s('dashboard.book.slotAt', { date: formatDate(at, 'full'), time: formatTime(at) }),
                  })}
                </div>
              )}
            </div>
          </div>

          {formError && <Alert variant="error" data-testid="reschedule-error">{formError}</Alert>}

          <FormField label={s('dashboard.reschedule.newTime')} error={whenError} htmlFor="reschedule-when">
            <JalaliDatePicker
              id="reschedule-when"
              value={when}
              onChange={(v) => { setWhen(v || ''); setWhenError(''); }}
              withTime
              minuteStep={5}
              disabled={busy}
              aria-invalid={whenError ? true : undefined}
              aria-label={s('dashboard.reschedule.newTime')}
              data-testid="reschedule-when"
            />
          </FormField>

          <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.8 }}>
            {s('dashboard.reschedule.hint')}
          </div>
        </form>
      </TaskOverlay>
      {guard}
    </>
  );
}
